import { keepPreviousData, MutationCache, QueryClient } from '@tanstack/react-query';
import { ApiError } from './http';
import { liveIsUp } from './liveStatus';
import { pushToast } from './toast';

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      /** The mutation shows its own error, so the global toast stays quiet. */
      silent?: boolean;
    };
  }
}

const POLL_WHEN_OFFLINE_MS = 30_000;

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation.meta?.silent) return;
      // A lapsed session already redirects to the login page.
      if (error instanceof ApiError && error.status === 401) return;
      pushToast('error', error instanceof Error ? error.message : 'That did not work. Try again.');
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 10 * 60_000,
      // Keep showing what was on screen while new data loads, so nothing flashes.
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: true,
      // Live events keep the screens fresh; when the socket is down, fall back to polling.
      refetchInterval: () => (liveIsUp() ? false : POLL_WHEN_OFFLINE_MS),
      retry: (failures, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failures < 2;
      },
    },
    mutations: { retry: 0 },
  },
});
